/**
 * Header + primary navigation behaviour.
 *
 * Rewritten from jQuery to vanilla JS to keep tap handlers cheap (INP): the
 * sticky header now uses an IntersectionObserver sentinel instead of a
 * throttled scroll listener that forced a reflow on every frame, the document
 * level click handlers bail on a cheap class check before touching the DOM, and
 * the menu toggle flips its visual state before doing any scrolling.
 */

const header = document.querySelector('.site-header');

/**
 * jQuery `:visible` equivalent.
 * @param {Element} el
 * @returns {boolean}
 */
function isVisible(el) {
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
}

/**
 * jQuery `.index()` equivalent — position amongst element siblings.
 * @param {Element} el
 * @returns {number}
 */
function elementIndex(el) {
    let index = 0;
    let sibling = el.previousElementSibling;
    while (sibling) {
        index += 1;
        sibling = sibling.previousElementSibling;
    }
    return index;
}

/**
 * Sticky header: an invisible sentinel is placed at the header's natural top.
 * When it scrolls out of view the header becomes fixed. Because the sentinel is
 * a real element in the flow, ad slots or notices that push the header down are
 * handled automatically — this replaces the old `triggerSlotsRendered` re-measure.
 */
function initStickyHeader() {
    const headerBar = header.querySelector('.header-bar');
    const mainNav = header.querySelector('.main-navigation');
    const subMenu = header.querySelector('.main-navigation .sub-navigation');
    const root = document.documentElement;

    const isOverlayOpen = () =>
        !!document.querySelector('.search-open') ||
        !!document.querySelector('.site-header--no-scroll') ||
        !!document.querySelector('.menu-select--open');

    const applyState = fixed => {
        // Don't flip the header while an overlay (search / mega-menu) is open.
        if (isOverlayOpen()) {
            return;
        }

        header.classList.toggle('site-header--fixed', fixed);

        const offset = fixed
            ? (headerBar ? headerBar.offsetHeight : 0) + (mainNav ? mainNav.offsetHeight : 0)
            : subMenu
            ? header.offsetHeight + subMenu.offsetHeight
            : header.offsetHeight;

        root.style.setProperty('--sidebar-offset', `${offset}px`);
    };

    if ('IntersectionObserver' in window) {
        const sentinel = document.createElement('div');
        sentinel.className = 'site-header__sentinel';
        sentinel.setAttribute('aria-hidden', 'true');
        header.parentNode.insertBefore(sentinel, header);

        const observer = new IntersectionObserver(
            entries => {
                entries.forEach(entry => applyState(!entry.isIntersecting));
            },
            { threshold: 0 },
        );

        observer.observe(sentinel);
    }

    // Heights change on breakpoint/orientation change; refresh the offset for
    // the current state without hammering the main thread.
    let resizeScheduled = false;
    window.addEventListener(
        'resize',
        () => {
            if (resizeScheduled) {
                return;
            }
            resizeScheduled = true;
            requestAnimationFrame(() => {
                resizeScheduled = false;
                applyState(header.classList.contains('site-header--fixed'));
            });
        },
        { passive: true },
    );
}

/**
 * Mobile burger toggle, search toggle, and outside-click-to-close.
 */
function initHeaderToggles() {
    const navToggle = document.querySelector('[data-behaviour="navigation-toggle"]');
    if (navToggle) {
        navToggle.addEventListener('click', e => {
            e.preventDefault();

            // Flip the visual state first so the frame can paint inside the
            // interaction window, then run the (compositor-driven) scroll.
            header.classList.toggle('site-header--open');
            document.body.classList.toggle('u-lock-scrolling');

            const top = header.getBoundingClientRect().top + window.scrollY;
            window.scrollTo({ top, behavior: 'smooth' });
        });
    }

    const searchToggle = document.querySelector('[data-behaviour="search-toggle"]');
    if (searchToggle) {
        searchToggle.addEventListener('click', e => {
            e.preventDefault();
            header.classList.toggle('serach-form--open');
        });
    }

    // Close the mobile nav when tapping outside the header. Bail on the cheap
    // class check before any DOM traversal so unrelated taps stay fast.
    document.addEventListener('click', e => {
        if (!header.classList.contains('site-header--open')) {
            return;
        }

        const mobileNav = document.querySelector('.mobile-navigation');
        if (!mobileNav || !isVisible(mobileNav) || header.contains(e.target)) {
            return;
        }

        header.classList.remove('site-header--open');
        document.body.classList.remove('u-lock-scrolling');
    });
}

/**
 * Keep clicks on sub-navigation links/selects from bubbling to the document
 * handlers that would otherwise close the open menu.
 */
function initSubNavigation() {
    document.querySelectorAll('.sub-navigation .menu-item').forEach(item => {
        item.addEventListener('click', e => {
            if (e.target.closest('a, select')) {
                e.stopPropagation();
            }
        });
    });
}

/**
 * Mobile accordion menu — expand/collapse items with children.
 */
function initMobileMenu() {
    const mobileNavItems = document.querySelectorAll('[data-behaviour="mobile-nav"] .menu-item-has-children > a');
    if (!mobileNavItems.length) {
        return;
    }

    mobileNavItems.forEach(mobileNavItem => {

        mobileNavItem.addEventListener('click', e => {

            e.preventDefault();

            const parentItem =
                mobileNavItem.parentElement && mobileNavItem.parentElement.matches('.menu-item-has-children')
                    ? mobileNavItem.parentElement
                    : null;

            if (parentItem) {
                // Close any open descendants before toggling this item.
                if (parentItem.classList.contains('open')) {
                    parentItem.querySelectorAll('.open').forEach(openEl => openEl.classList.remove('open'));
                }
                parentItem.classList.toggle('open');
            }

            const menu = mobileNavItem.closest('.mobile-menu');
            if (!menu) {
                return;
            }

            if (menu.querySelectorAll('.open').length < 1) {
                menu.classList.remove('open');
            } else {
                menu.classList.add('open');
            }

            const depth = mobileNavItem.dataset.depth ? parseInt(mobileNavItem.dataset.depth, 10) : 0;
            if (depth === 0) {
                menu.classList.remove('open--1', 'open--2', 'open--3');
            } else if (depth === 1) {
                menu.classList.remove('open--2', 'open--3');
            } else if (depth === 2) {
                menu.classList.remove('open--3');
            }
            menu.classList.toggle(`open--${depth}`);
        });
    });
}

if (header) {
    initStickyHeader();
    initHeaderToggles();
    initSubNavigation();
    initMobileMenu();
}